import React, { ReactElement, cloneElement } from 'react';

import { Anchor, Box, Button, DefaultProps, Flex, Text, Title, useMantineTheme } from '@mantine/core';
import { IconExternalLink } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

interface OnboardingStepTemplateProps {
  title: {
    text: string;
    icon?: ReactElement;
  };
  description: string;
  learnMore?: {
    link: string;
    onClick?: () => void;
  };
  additionalLinks?: {
    title: string;
    links: {
      title: string;
      href: string;
      icon: ReactElement;
      onClick?: () => void;
    }[];
  };
  additionalElement?: ReactElement<DefaultProps>;
  rightElement?: ReactElement<DefaultProps>;
}

export function OnboardingStepTemplate({
  title,
  description,
  learnMore,
  additionalLinks,
  additionalElement,
  rightElement
}: OnboardingStepTemplateProps) {
  const theme = useMantineTheme();
  const { t } = useTranslation();

  return (
    <Flex direction="column" justify="center" h="100%" w="100%" px={{ base: 'md', md: 0 }}>
      <Flex
        direction={{ base: 'column', md: 'row' }}
        justify={{ base: 'center', md: 'space-between' }}
        align="center"
        gap={{ base: 40, md: 20 }}
        w="100%">
        <Box maw={{ base: '100%', md: '50%' }}>
          <Flex direction="row" align="center" gap={10}>
            {title.icon && cloneElement(title.icon, { width: 20, height: 20 })}
            <Title order={1}>{title.text}</Title>
          </Flex>
          <Text color={theme.other.text.dimmed} my={10} maw={{ base: '100%', md: '90%' }}>
            {description}
          </Text>
          {learnMore && (
            <Button
              component="a"
              href={learnMore.link}
              target="_blank"
              variant="default"
              size="xs"
              bg="transparent"
              onClick={learnMore.onClick}
              rightIcon={<IconExternalLink />}>
              {t('APP.MORE')}
            </Button>
          )}
          {additionalLinks && (
            <Flex direction="column" gap={5} w="fit-content" mt={20}>
              <Text mb={5}>{additionalLinks.title}</Text>
              {additionalLinks.links.map((link, index) => (
                <Anchor
                  key={index}
                  href={link.href}
                  target="_blank"
                  color={theme.other.text.dimmed}
                  onClick={link.onClick}>
                  <Flex direction="row" align="center" gap={5}>
                    {cloneElement(link.icon, { width: 14, height: 14 })}
                    <Text>{link.title}</Text>
                  </Flex>
                </Anchor>
              ))}
            </Flex>
          )}
          {additionalElement && (
            <Box mt={20}>
              {cloneElement(additionalElement, {
                w: additionalElement.props.w || '100%',
                h: additionalElement.props.h || '100%'
              })}
            </Box>
          )}
        </Box>
        {rightElement && (
          <Flex w={{ base: '100%', md: '50%' }}>
            {cloneElement(rightElement, {
              m: rightElement.props.m || 'auto',
              w: rightElement.props.w || '100%',
              maw: rightElement.props.maw || 400
            })}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}
